import { FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { ArrowRight, Calculator, CheckCircle2, Gauge, Route } from 'lucide-react';
import { LeadCapture } from '../components/LeadCapture';
import { saveToolHandoff } from '../toolHandoff';
import { trackToolEvent } from '../toolTracking';

export type ToolSlug = 'diagnostic-projet' | 'orientation-motorisation' | 'comparateur-cout-global';

const toolCards = [
  {
    slug: 'diagnostic-projet' as const,
    icon: Route,
    index: '01',
    stage: 'Cadrer',
    title: 'Diagnostic de projet automobile',
    situation: 'Je ne sais pas encore où concentrer ma recherche.',
    description: 'Cinq questions pour repérer ce qui bloque et choisir la prochaine étape utile.',
    output: 'Un niveau de maturité et une action concrète.',
    after: 'Motorisation, méthode de comparaison ou accompagnement.',
    duration: '3 min',
  },
  {
    slug: 'orientation-motorisation' as const,
    icon: Gauge,
    index: '02',
    stage: 'Orienter',
    title: 'Orientation de motorisation',
    situation: 'J’hésite entre essence, hybride, rechargeable ou électrique.',
    description: 'Votre kilométrage, vos trajets et votre recharge deviennent des pistes à étudier.',
    output: 'Trois familles de motorisation argumentées.',
    after: 'Chercher des modèles cohérents puis comparer deux pistes.',
    duration: '4 min',
  },
  {
    slug: 'comparateur-cout-global' as const,
    icon: Calculator,
    index: '03',
    stage: 'Départager',
    title: 'Comparateur de coût global',
    situation: 'J’ai déjà deux voitures en tête et le prix ne suffit plus.',
    description: 'Deux véhicules ramenés à la même durée avec vos hypothèses de coût.',
    output: 'Un coût estimé, son écart et ses limites.',
    after: 'Vérifier les hypothèses et les exemplaires avant de décider.',
    duration: '6 min',
  },
];

const ToolContactLink = ({
  tool,
  title,
  summary,
  label,
}: {
  tool: ToolSlug;
  title: string;
  summary: string;
  label: string;
}) => (
  <a
    className="as-button as-button-lime"
    href="/#contact"
    onClick={() => {
      saveToolHandoff({ tool, title, summary });
      trackToolEvent(tool, 'contact');
    }}
  >
    {label} <ArrowRight size={17} aria-hidden="true" />
  </a>
);

const ResultChoices = ({
  tool,
  resultText,
  nextHref,
  nextTitle,
  nextText,
  nextLabel,
  helpTitle,
  helpText,
  contactLabel,
}: {
  tool: ToolSlug;
  resultText: string;
  nextHref: string;
  nextTitle: string;
  nextText: string;
  nextLabel: string;
  helpTitle: string;
  helpText: string;
  contactLabel: string;
}) => {
  const card = toolCards.find((item) => item.slug === tool)!;
  return (
    <section className="as-result-choices" aria-label="Que faire après ce résultat ?">
      <div>
        <span>Continuer seul</span>
        <strong>{nextTitle}</strong>
        <p>{nextText}</p>
        <a className="as-result-link" href={nextHref}>{nextLabel} <ArrowRight size={15} aria-hidden="true" /></a>
      </div>
      <div className="as-result-help">
        <span>Avec Auto Sens</span>
        <strong>{helpTitle}</strong>
        <p>{helpText}</p>
        <ToolContactLink tool={tool} title={card.title} summary={resultText} label={contactLabel} />
      </div>
    </section>
  );
};

export const ToolsPage = () => (
  <main id="top" className="as-tools-page">
    <header className="as-tools-hero">
      <div>
        <p className="as-descriptor">Outils gratuits · résultat immédiat</p>
        <h1>Avancer selon l’étape réelle de votre projet.</h1>
        <p className="as-tools-hero-intro">
          Vous n’avez pas besoin de remplir les trois outils. Entrez au niveau qui correspond à votre
          situation, obtenez un résultat, puis choisissez de continuer seul ou de me transmettre ce contexte.
        </p>
      </div>
      <aside className="as-tools-start" aria-labelledby="tools-start-title">
        <div><span>Point de départ conseillé</span><Route size={28} aria-hidden="true" /></div>
        <h2 id="tools-start-title">Vous hésitez sur l’outil ? Commencez par le diagnostic.</h2>
        <p>En trois minutes, il vous redirige vers la suite la plus utile sans demander d’adresse e-mail.</p>
        <a className="as-button as-button-lime" href="/outils/diagnostic-projet/">Faire le diagnostic <ArrowRight size={17} aria-hidden="true" /></a>
        <footer>
          <span>Projet déjà cadré ?</span>
          <a href="/outils/orientation-motorisation/">Choisir une énergie</a>
          <a href="/outils/comparateur-cout-global/">Comparer deux voitures</a>
        </footer>
      </aside>
    </header>

    <section id="outils" className="as-tool-index" aria-labelledby="tool-index-title">
      <div className="as-tool-index-heading">
        <span className="as-kicker">Votre chemin</span>
        <h2 id="tool-index-title">Choisissez votre situation, pas un outil au hasard.</h2>
        <p>Les étapes sont ordonnées, mais vous pouvez entrer directement là où se trouve votre décision.</p>
      </div>
      <div className="as-tool-path-list">
        {toolCards.map((tool) => (
          <article className="as-tool-path-item" key={tool.slug}>
            <div className="as-tool-path-marker"><span>{tool.index}</span><tool.icon size={25} aria-hidden="true" /></div>
            <div className="as-tool-path-copy">
              <p>{tool.situation}</p>
              <h3>{tool.stage} — {tool.title}</h3>
              <span>{tool.description}</span>
            </div>
            <dl>
              <div><dt>Vous obtenez</dt><dd>{tool.output}</dd></div>
              <div><dt>Ensuite</dt><dd>{tool.after}</dd></div>
            </dl>
            <div className="as-tool-path-action">
              <span>{tool.duration}</span>
              <a href={`/outils/${tool.slug}/`}>Commencer <ArrowRight size={15} aria-hidden="true" /></a>
            </div>
          </article>
        ))}
      </div>
    </section>

    <section className="as-tool-disclaimer">
      <strong>Une aide au tri, pas un verdict</strong>
      <p>
        Ces outils ne diagnostiquent pas l’état d’un véhicule, ne prédisent pas sa valeur future et ne
        remplacent ni un devis d’assurance, ni un contrat, ni une inspection physique. Ils organisent vos
        hypothèses pour faire apparaître la prochaine décision utile.
      </p>
    </section>
  </main>
);

const ToolShell = ({ slug, children }: { slug: ToolSlug; children: ReactNode }) => {
  const card = toolCards.find((item) => item.slug === slug)!;
  useEffect(() => trackToolEvent(slug, 'view'), [slug]);
  return (
    <main id="top" className="as-tool-page">
      <nav className="as-breadcrumb" aria-label="Fil d’Ariane"><a href="/">Accueil</a><span>/</span><a href="/outils/">Outils</a><span>/</span><span aria-current="page">{card.title}</span></nav>
      <header className="as-tool-hero">
        <div className="as-tool-hero-copy">
          <p className="as-descriptor">{card.stage} · outil gratuit · {card.duration}</p>
          <h1>{card.title}</h1>
          <p>{card.description}</p>
        </div>
        <aside className="as-tool-brief" aria-label="Fonctionnement de cet outil">
          <div className="as-tool-brief-index"><span>{card.index}</span><card.icon size={29} aria-hidden="true" /></div>
          <dl>
            <div><dt>À utiliser si</dt><dd>{card.situation}</dd></div>
            <div><dt>Vous obtenez</dt><dd>{card.output}</dd></div>
            <div><dt>Après le résultat</dt><dd>{card.after}</dd></div>
          </dl>
        </aside>
      </header>
      <nav className="as-tool-journey" aria-label="Étapes des outils Auto Sens">
        <p>Entrez à l’étape qui correspond à votre projet.</p>
        <ol>
          {toolCards.map((tool) => (
            <li key={tool.slug} className={tool.slug === slug ? 'is-current' : ''}>
              <a href={`/outils/${tool.slug}/`} aria-current={tool.slug === slug ? 'step' : undefined}>
                <span>{tool.index}</span><strong>{tool.stage}</strong>
              </a>
            </li>
          ))}
        </ol>
      </nav>
      {children}
    </main>
  );
};

const ToolFormHeading = ({ title, text, meta }: { title: string; text: string; meta: string }) => (
  <header className="as-tool-form-heading">
    <span>{meta}</span>
    <h2>{title}</h2>
    <p>{text}</p>
  </header>
);

type DiagnosticInputs = {
  deadline: string;
  budget: string;
  comparison: string;
  constraints: string;
  acquisition: string;
};

type DiagnosticResult = {
  level: string;
  title: string;
  text: string;
  next: string;
  nextHref: string;
  nextTitle: string;
  nextText: string;
  nextLabel: string;
  helpTitle: string;
  helpText: string;
  contactLabel: string;
};

const DiagnosticProject = () => {
  const [inputs, setInputs] = useState<DiagnosticInputs>({ deadline: '', budget: '', comparison: '', constraints: '', acquisition: '' });
  const [result, setResult] = useState<DiagnosticResult | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!result) trackToolEvent('diagnostic-projet', 'result');
    let score = 0;
    if (inputs.deadline === 'urgent') score += 2;
    if (inputs.budget === 'unclear') score += 2;
    if (inputs.comparison === 'many') score += 2;
    if (inputs.constraints === 'many') score += 2;
    if (inputs.acquisition === 'compare') score += 2;
    if (score >= 7) {
      setResult({
        level: 'Projet complexe',
        title: 'Vous avez surtout besoin d’un arbitrage.',
        text: 'Plusieurs contraintes ou scénarios se croisent. Le risque principal est de comparer beaucoup d’annonces sans base commune.',
        next: 'Formalisez les non-négociables, puis comparez trois à cinq pistes sur la même durée.',
        nextHref: '/conseils/comparer-deux-voitures-occasion/',
        nextTitle: 'Construire une grille commune',
        nextText: 'La méthode de comparaison vous aide à séparer les preuves, les coûts et les préférences.',
        nextLabel: 'Lire la méthode',
        helpTitle: 'Faire cadrer le projet avant les annonces',
        helpText: 'Je reprends vos contraintes pour construire un cahier des charges et réduire le champ de recherche.',
        contactLabel: 'Faire cadrer mon projet',
      });
    } else if (score >= 4) {
      setResult({
        level: 'Projet à cadrer',
        title: 'La bonne catégorie n’est pas encore totalement fixée.',
        text: 'Votre recherche peut avancer seul, mais quelques hypothèses doivent être posées avant d’évaluer les modèles.',
        next: 'Écrivez votre semaine type, votre horizon de détention et trois compromis acceptables.',
        nextHref: '/outils/orientation-motorisation/',
        nextTitle: 'Clarifier la motorisation',
        nextText: 'Vos kilomètres, vos trajets et votre recharge permettent déjà d’écarter certaines pistes.',
        nextLabel: 'Tester mon usage',
        helpTitle: 'Transformer les contraintes en shortlist',
        helpText: 'Je peux reprendre ce diagnostic, approfondir votre usage et hiérarchiser des véhicules cohérents.',
        contactLabel: 'Construire ma shortlist',
      });
    } else {
      setResult({
        level: 'Projet déjà bien cadré',
        title: 'Vous pouvez concentrer la recherche sur les exemplaires.',
        text: 'Les grandes décisions semblent prises. La valeur vient maintenant de la comparaison des versions, des historiques et des coûts prévisibles.',
        next: 'Préparez une grille unique et un seuil d’abandon avant le premier déplacement.',
        nextHref: '/outils/comparateur-cout-global/',
        nextTitle: 'Départager deux voitures',
        nextText: 'Ramenez vos deux meilleures pistes à la même durée avec les mêmes hypothèses.',
        nextLabel: 'Comparer mes deux pistes',
        helpTitle: 'Passer du cahier des charges aux bons modèles',
        helpText: 'Je peux chercher les versions pertinentes, leurs points d’attention et des annonces repères.',
        contactLabel: 'Obtenir une shortlist',
      });
    }
  };

  const resultText = result ? `${result.level} — ${result.title}\n${result.text}\nProchaine étape : ${result.next}` : '';

  return (
    <ToolShell slug="diagnostic-projet">
      <section className="as-tool-workspace">
        <form className="as-tool-form" onSubmit={submit}>
          <ToolFormHeading title="Situer votre projet" text="Répondez selon la situation actuelle, même si certaines décisions restent ouvertes." meta="5 questions · aucune donnée personnelle" />
          <fieldset><legend>1. Quand souhaitez-vous acheter ?</legend><select required value={inputs.deadline} onChange={(event) => setInputs({ ...inputs, deadline: event.target.value })}><option value="">Choisir</option><option value="urgent">Sous un mois</option><option value="normal">Dans 1 à 3 mois</option><option value="open">Sans date précise</option></select></fieldset>
          <fieldset><legend>2. Votre budget global est-il fixé ?</legend><select required value={inputs.budget} onChange={(event) => setInputs({ ...inputs, budget: event.target.value })}><option value="">Choisir</option><option value="clear">Oui, achat et marge compris</option><option value="price">J’ai seulement un prix d’achat</option><option value="unclear">Non, ou je raisonne en mensualité</option></select></fieldset>
          <fieldset><legend>3. Combien de pistes hésitez-vous à comparer ?</legend><select required value={inputs.comparison} onChange={(event) => setInputs({ ...inputs, comparison: event.target.value })}><option value="">Choisir</option><option value="one">Un modèle ou une catégorie</option><option value="few">Deux à trois pistes</option><option value="many">Quatre pistes ou davantage</option></select></fieldset>
          <fieldset><legend>4. Combien de contraintes doivent cohabiter ?</legend><select required value={inputs.constraints} onChange={(event) => setInputs({ ...inputs, constraints: event.target.value })}><option value="">Choisir</option><option value="few">Une ou deux</option><option value="some">Trois à quatre</option><option value="many">Cinq ou davantage</option></select></fieldset>
          <fieldset><legend>5. Le mode d’acquisition est-il décidé ?</legend><select required value={inputs.acquisition} onChange={(event) => setInputs({ ...inputs, acquisition: event.target.value })}><option value="">Choisir</option><option value="yes">Oui</option><option value="open">Non, mais sans offre à comparer</option><option value="compare">J’ai plusieurs offres ou mensualités à comparer</option></select></fieldset>
          <button className="as-button as-button-dark" type="submit">Voir mon diagnostic <ArrowRight size={17} aria-hidden="true" /></button>
        </form>
        <div className="as-tool-result-column" aria-live="polite">
          {result ? (
            <>
              <article className="as-tool-result">
                <span>{result.level}</span><h2>{result.title}</h2><p>{result.text}</p>
                <div className="as-result-next"><strong>Votre prochaine étape</strong><p>{result.next}</p></div>
                <ResultChoices tool="diagnostic-projet" resultText={resultText} nextHref={result.nextHref} nextTitle={result.nextTitle} nextText={result.nextText} nextLabel={result.nextLabel} helpTitle={result.helpTitle} helpText={result.helpText} contactLabel={result.contactLabel} />
              </article>
              <LeadCapture tool="diagnostic-projet" resultText={resultText} inputs={inputs} />
            </>
          ) : <div className="as-tool-empty"><CheckCircle2 size={26} aria-hidden="true" /><strong>Votre résultat apparaîtra ici.</strong><p>Il indiquera quoi faire ensuite, que vous souhaitiez continuer seul ou vous faire accompagner.</p></div>}
        </div>
      </section>
    </ToolShell>
  );
};

type MotorInputs = { annualKm: number; dailyKm: number; cityPercent: number; longTrips: number; charging: string };

const motorLabels = {
  electric: 'Électrique',
  hybrid: 'Hybride non rechargeable',
  petrol: 'Essence',
  diesel: 'Diesel',
  plugin: 'Hybride rechargeable',
} as const;

const MotorizationOrientation = () => {
  const [inputs, setInputs] = useState<MotorInputs>({ annualKm: 15000, dailyKm: 40, cityPercent: 35, longTrips: 2, charging: '' });
  const [calculated, setCalculated] = useState(false);
  const ranking = useMemo(() => {
    const scores: Record<keyof typeof motorLabels, number> = { electric: 0, hybrid: 0, petrol: 0, diesel: -1, plugin: -1 };
    if (inputs.annualKm < 12000) { scores.petrol += 4; scores.hybrid += 1; scores.diesel -= 3; }
    if (inputs.annualKm >= 12000 && inputs.annualKm <= 25000) { scores.hybrid += 2; scores.electric += 1; }
    if (inputs.annualKm > 25000) { scores.diesel += 3; scores.electric += 2; scores.petrol -= 1; }
    if (inputs.cityPercent >= 40) { scores.hybrid += 4; scores.electric += 3; scores.diesel -= 2; }
    if (inputs.dailyKm <= 70) { scores.electric += 3; scores.plugin += 2; }
    if (inputs.longTrips >= 4) { scores.diesel += 2; scores.petrol += 1; scores.electric -= 1; }
    if (inputs.charging === 'home-work') { scores.electric += 5; scores.plugin += 4; }
    if (inputs.charging === 'public') { scores.electric -= 1; scores.plugin -= 2; }
    if (inputs.charging === 'none') { scores.electric -= 5; scores.plugin -= 6; scores.hybrid += 2; scores.petrol += 1; }
    return (Object.entries(scores) as [keyof typeof motorLabels, number][]).sort((a, b) => b[1] - a[1]);
  }, [inputs]);

  const reasons: Record<keyof typeof motorLabels, string> = {
    electric: inputs.charging === 'home-work' ? 'Recharge régulière et trajets quotidiens compatibles à confirmer avec l’autonomie hivernale.' : 'À retenir seulement avec une recharge publique réellement fiable et compatible avec votre routine.',
    hybrid: 'Bonne piste lorsque la ville et les trajets mixtes pèsent dans l’année, sans dépendre d’une prise.',
    petrol: 'Piste simple à comparer lorsque le kilométrage reste modéré ou les longs trajets peu fréquents.',
    diesel: 'Peut rester cohérent pour un fort kilométrage très routier ; restrictions locales et entretien doivent être vérifiés.',
    plugin: inputs.charging === 'home-work' ? 'À chiffrer si la batterie peut être rechargée presque chaque jour et couvrir l’essentiel des trajets courts.' : 'Peu cohérent sans recharge fréquente : le surcoût et la masse restent présents batterie vide.',
  };
  const resultText = calculated
    ? ranking.slice(0, 3).map(([key], index) => `${index + 1}. ${motorLabels[key]} — ${reasons[key]}`).join('\n')
    : '';

  return (
    <ToolShell slug="orientation-motorisation">
      <section className="as-tool-workspace">
        <form className="as-tool-form" onSubmit={(event) => { event.preventDefault(); if (!calculated) trackToolEvent('orientation-motorisation', 'result'); setCalculated(true); }}>
          <ToolFormHeading title="Décrire une année normale" text="Une estimation honnête est plus utile qu’un chiffre trop précis construit pour justifier une motorisation." meta="5 données · classement indicatif" />
          <fieldset><legend>Kilométrage annuel</legend><input type="number" min="1000" max="100000" step="1000" required value={inputs.annualKm} onChange={(event) => setInputs({ ...inputs, annualKm: Number(event.target.value) })} /><small>Une estimation à ± 2 000 km suffit.</small></fieldset>
          <fieldset><legend>Distance d’une journée habituelle</legend><input type="number" min="1" max="800" required value={inputs.dailyKm} onChange={(event) => setInputs({ ...inputs, dailyKm: Number(event.target.value) })} /><small>Aller-retour total, en kilomètres.</small></fieldset>
          <fieldset><legend>Part de ville estimée</legend><input type="number" min="0" max="100" step="5" required value={inputs.cityPercent} onChange={(event) => setInputs({ ...inputs, cityPercent: Number(event.target.value) })} /><small>En pourcentage de vos kilomètres.</small></fieldset>
          <fieldset><legend>Longs trajets par mois</legend><input type="number" min="0" max="30" required value={inputs.longTrips} onChange={(event) => setInputs({ ...inputs, longTrips: Number(event.target.value) })} /><small>Trajets pour lesquels l’autonomie ou le ravitaillement change votre organisation.</small></fieldset>
          <fieldset><legend>Recharge régulière possible</legend><select required value={inputs.charging} onChange={(event) => setInputs({ ...inputs, charging: event.target.value })}><option value="">Choisir</option><option value="home-work">À domicile ou au travail</option><option value="public">Seulement sur borne publique</option><option value="none">Non</option></select></fieldset>
          <button className="as-button as-button-dark" type="submit">Comparer les pistes <ArrowRight size={17} aria-hidden="true" /></button>
        </form>
        <div className="as-tool-result-column" aria-live="polite">
          {calculated ? (
            <>
              <article className="as-tool-result as-motor-result">
                <span>Orientation, pas verdict</span><h2>Trois pistes à confronter au marché.</h2>
                <ol>{ranking.slice(0, 3).map(([key], index) => <li key={key}><b>{index + 1}</b><div><strong>{motorLabels[key]}</strong><p>{reasons[key]}</p></div></li>)}</ol>
                <p className="as-result-limit">Hypothèse : vos réponses décrivent une année habituelle. Prix, assurance, stationnement, restrictions locales et modèles disponibles peuvent modifier l’ordre.</p>
                <ResultChoices
                  tool="orientation-motorisation"
                  resultText={resultText}
                  nextHref="/outils/comparateur-cout-global/"
                  nextTitle="Comparer deux modèles concrets"
                  nextText="Une famille de motorisation ne suffit pas : confrontez maintenant deux véhicules à votre durée et votre budget."
                  nextLabel="Ouvrir le comparateur"
                  helpTitle="Transformer ces pistes en shortlist"
                  helpText="Je cherche les modèles, versions et compromis cohérents avec votre usage, puis je les documente."
                  contactLabel="Obtenir une shortlist"
                />
              </article>
              <LeadCapture tool="orientation-motorisation" resultText={resultText} inputs={inputs} />
            </>
          ) : <div className="as-tool-empty"><Gauge size={26} aria-hidden="true" /><strong>Cinq données suffisent pour commencer.</strong><p>Le résultat comparera des familles de motorisation, puis vous indiquera comment passer aux modèles.</p></div>}
        </div>
      </section>
    </ToolShell>
  );
};

type VehicleCost = { name: string; price: number; consumption: number; energyPrice: number; maintenance: number; insurance: number; resale: number };
type CostInputs = { years: number; annualKm: number; a: VehicleCost; b: VehicleCost };

const formatEuro = (value: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
const totalCost = (vehicle: VehicleCost, years: number, annualKm: number) => {
  const energy = annualKm * years * vehicle.consumption / 100 * vehicle.energyPrice;
  return { energy, total: vehicle.price - vehicle.resale + energy + (vehicle.maintenance + vehicle.insurance) * years };
};

const VehicleCostFields = ({ label, vehicle, onChange }: { label: string; vehicle: VehicleCost; onChange: (vehicle: VehicleCost) => void }) => (
  <fieldset className="as-cost-vehicle">
    <legend>{label}</legend>
    <label className="as-cost-name">Nom ou modèle<input required value={vehicle.name} onFocus={(event) => event.currentTarget.select()} onChange={(event) => onChange({ ...vehicle, name: event.target.value })} /></label>
    <div className="as-cost-field-group">
      <span>Achat et revente</span>
      <div>
        <label>Prix d’achat (€)<input type="number" min="0" step="100" required value={vehicle.price} onFocus={(event) => event.currentTarget.select()} onChange={(event) => onChange({ ...vehicle, price: Number(event.target.value) })} /></label>
        <label>Revente estimée à la fin (€)<input type="number" min="0" step="100" required value={vehicle.resale} onFocus={(event) => event.currentTarget.select()} onChange={(event) => onChange({ ...vehicle, resale: Number(event.target.value) })} /></label>
      </div>
    </div>
    <div className="as-cost-field-group">
      <span>Énergie</span>
      <div>
        <label>Consommation / 100 km<input type="number" min="0" step="0.1" required value={vehicle.consumption} onFocus={(event) => event.currentTarget.select()} onChange={(event) => onChange({ ...vehicle, consumption: Number(event.target.value) })} /><small>Litres ou kWh selon le véhicule.</small></label>
        <label>Prix de l’énergie (€ / L ou kWh)<input type="number" min="0" step="0.01" required value={vehicle.energyPrice} onFocus={(event) => event.currentTarget.select()} onChange={(event) => onChange({ ...vehicle, energyPrice: Number(event.target.value) })} /></label>
      </div>
    </div>
    <div className="as-cost-field-group">
      <span>Dépenses annuelles</span>
      <div>
        <label>Entretien + pneus (€)<input type="number" min="0" step="50" required value={vehicle.maintenance} onFocus={(event) => event.currentTarget.select()} onChange={(event) => onChange({ ...vehicle, maintenance: Number(event.target.value) })} /></label>
        <label>Assurance (€)<input type="number" min="0" step="50" required value={vehicle.insurance} onFocus={(event) => event.currentTarget.select()} onChange={(event) => onChange({ ...vehicle, insurance: Number(event.target.value) })} /></label>
      </div>
    </div>
  </fieldset>
);

const CostComparator = () => {
  const [inputs, setInputs] = useState<CostInputs>({
    years: 5,
    annualKm: 15000,
    a: { name: 'Voiture A — exemple', price: 22000, consumption: 5.8, energyPrice: 1.85, maintenance: 700, insurance: 750, resale: 11000 },
    b: { name: 'Voiture B — exemple', price: 26000, consumption: 4.8, energyPrice: 1.85, maintenance: 800, insurance: 850, resale: 14000 },
  });
  const [calculated, setCalculated] = useState(false);
  const [hasCustomData, setHasCustomData] = useState(false);
  const update = (next: CostInputs) => { setInputs(next); setHasCustomData(true); };
  const a = totalCost(inputs.a, inputs.years, inputs.annualKm);
  const b = totalCost(inputs.b, inputs.years, inputs.annualKm);
  const winner = a.total <= b.total ? { vehicle: inputs.a, result: a } : { vehicle: inputs.b, result: b };
  const difference = Math.abs(a.total - b.total);
  const resultText = calculated
    ? `${inputs.a.name} : ${formatEuro(a.total)} sur ${inputs.years} ans, dont ${formatEuro(a.energy)} d’énergie estimée.\n${inputs.b.name} : ${formatEuro(b.total)} sur ${inputs.years} ans, dont ${formatEuro(b.energy)} d’énergie estimée.\nÉcart estimé : ${formatEuro(difference)}. Financement, fiscalité, réparations imprévues et variation du prix de l’énergie exclus.`
    : '';

  return (
    <ToolShell slug="comparateur-cout-global">
      <section className="as-cost-workspace">
        <form id="cost-comparison-form" className="as-cost-form" onSubmit={(event) => { event.preventDefault(); if (!calculated) trackToolEvent('comparateur-cout-global', 'result'); setCalculated(true); }}>
          <ToolFormHeading title="Mettre deux voitures sur la même durée" text="Les valeurs affichées servent uniquement à montrer le fonctionnement. Remplacez-les par vos prix, devis et estimations." meta="2 véhicules · 14 hypothèses visibles" />
          <div className="as-cost-demo" role="note">
            <div><strong>Données de démonstration</strong><p>Tant que vous ne modifiez rien, le résultat illustre seulement la méthode et ne doit pas guider un achat.</p></div>
            <span>{hasCustomData ? 'Vos modifications sont prises en compte.' : 'Cliquez dans un champ pour remplacer sa valeur.'}</span>
          </div>
          <div className="as-cost-common">
            <label>Durée de détention (années)<input type="number" min="1" max="15" required value={inputs.years} onFocus={(event) => event.currentTarget.select()} onChange={(event) => update({ ...inputs, years: Number(event.target.value) })} /></label>
            <label>Kilomètres par an<input type="number" min="1000" max="100000" step="1000" required value={inputs.annualKm} onFocus={(event) => event.currentTarget.select()} onChange={(event) => update({ ...inputs, annualKm: Number(event.target.value) })} /></label>
          </div>
          <div className="as-cost-columns">
            <VehicleCostFields label="Voiture A" vehicle={inputs.a} onChange={(vehicle) => update({ ...inputs, a: vehicle })} />
            <VehicleCostFields label="Voiture B" vehicle={inputs.b} onChange={(vehicle) => update({ ...inputs, b: vehicle })} />
          </div>
          <button className="as-button as-button-dark" type="submit">Calculer la comparaison <ArrowRight size={17} aria-hidden="true" /></button>
        </form>
        {calculated && (
          <div className="as-cost-output" aria-live="polite">
            <article className="as-tool-result">
              <span>{hasCustomData ? 'Estimation' : 'Démonstration'} sur {inputs.years} ans · {new Intl.NumberFormat('fr-FR').format(inputs.annualKm * inputs.years)} km</span>
              <h2>{winner.vehicle.name} ressort à {formatEuro(winner.result.total)}.</h2>
              <p>L’écart estimé est de <strong>{formatEuro(difference)}</strong>. Un faible écart doit être traité comme une zone d’incertitude, pas comme un gagnant certain.</p>
              <dl className="as-cost-results"><div><dt>{inputs.a.name}</dt><dd>{formatEuro(a.total)}</dd><small>dont énergie : {formatEuro(a.energy)}</small></div><div><dt>{inputs.b.name}</dt><dd>{formatEuro(b.total)}</dd><small>dont énergie : {formatEuro(b.energy)}</small></div></dl>
              <p className="as-result-limit">Calcul : achat − revente estimée + énergie + entretien/pneus + assurance. Financement, fiscalité, réparations imprévues et variation des prix sont exclus.</p>
              {hasCustomData ? (
                <ResultChoices
                  tool="comparateur-cout-global"
                  resultText={resultText}
                  nextHref="/conseils/verifier-voiture-occasion/"
                  nextTitle="Vérifier les deux exemplaires"
                  nextText="Le coût ne tranche pas l’historique, l’état, les équipements ni l’adéquation à votre usage."
                  nextLabel="Voir les contrôles avant achat"
                  helpTitle="Faire relire la comparaison"
                  helpText="Je reprends vos hypothèses, leurs sources et les versions comparées avant de les intégrer à une recommandation."
                  contactLabel="Transmettre ma comparaison"
                />
              ) : (
                <div className="as-demo-result-callout">
                  <strong>Ce résultat est une démonstration.</strong>
                  <p>Remplacez les deux noms et les valeurs avant d’enregistrer ou de transmettre cette comparaison.</p>
                  <a className="as-result-link" href="#cost-comparison-form">Renseigner mes voitures <ArrowRight size={15} aria-hidden="true" /></a>
                </div>
              )}
            </article>
            {hasCustomData && <LeadCapture tool="comparateur-cout-global" resultText={resultText} inputs={inputs} />}
          </div>
        )}
      </section>
    </ToolShell>
  );
};

export const ToolPage = ({ tool }: { tool: ToolSlug }) => {
  if (tool === 'orientation-motorisation') return <MotorizationOrientation />;
  if (tool === 'comparateur-cout-global') return <CostComparator />;
  return <DiagnosticProject />;
};
